import React from 'react';
import Head from 'next/head';
import get from 'lodash.get';

import {
  buildBlogPosting,
  buildDataset,
  buildLiveBlog,
  buildNewsArticle,
} from 'lib/LDJson';
import { shareImage } from 'lib/social';
import { getCanonical } from 'lib/urlUtils';

const hasMetaDataField = (source, path, oneOf = []) => {
  const field = get(source, path, false);
  if (field && typeof field === 'object') {
    return oneOf.some((key) => typeof field[key] !== 'undefined' && !!field[key]);
  }
  return false;
};

function constructMetaData(article, card, vertical) {
  // Constant article data
  const {
    nativeAd,
    primaryImage,
    searchable,
    socialImage,
    teaseImage,
    url,
  } = article;

  // Constant card data
  const {
    cardData: {
      teaseImage: cardTeaseImage,
    } = {},
  } = card || {};

  // Fields overwritable by card
  let { description, headline } = article;
  const fields = ['seo', 'social', 'primary'];

  if (card) {
    // Overwrite with card description
    if (hasMetaDataField(card, 'cardData.description', fields)) {
      ({ description } = card.cardData);
    } else if (hasMetaDataField(card, 'description', fields)) {
      ({ description } = card);
    }
    // Overwrite with card headline
    if (hasMetaDataField(card, 'cardData.headline', fields)) {
      ({ headline } = card.cardData);
    } else if (hasMetaDataField(card, 'headline', fields)) {
      ({ headline } = card);
    }
  }

  // Image
  const image = cardTeaseImage || socialImage || teaseImage || primaryImage || {};
  const imageUrl = (image.url && image.url.primary);

  // URLs
  const primaryURL = getCanonical(url);

  return (
    <Head>
      <title>{headline.seo || headline.primary}</title>
      <meta name="description" content={description.seo || description.primary} />
      <link rel="canonical" href={primaryURL} />

      <meta property="og:url" content={primaryURL} />
      <meta property="og:title" content={headline.social || headline.primary} />
      <meta property="og:description" content={description.primary || description.seo} />

      <meta property="og:image" content={shareImage(imageUrl, vertical, 'og')} />
      <meta property="og:image:secure_url" content={shareImage(imageUrl, vertical, 'og')} />
      <meta property="og:image:type" content={image.encodingFormat || 'image/gif'} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:type" content="article" />

      <meta name="twitter:image:src" content={shareImage(imageUrl, vertical, 'twitter')} />
      <meta name="twitter:image" content={shareImage(imageUrl, vertical, 'twitter')} />
      <meta name="twitter:title" content={headline.social || headline.primary} />
      <meta name="twitter:description" content={description.primary || description.seo} />
      <meta name="twitter:card" content="summary_large_image" />

      <meta name="thumbnail" content={shareImage(imageUrl, vertical)} />
      {(nativeAd || !searchable) && <meta name="ROBOTS" content="NOINDEX, NOFOLLOW" />}
    </Head>
  );
}

function getLiveBlogMetaData(article, card, vertical, isRegularBlog) {
  let liveBlogLDJson;

  if (card) {
    // Use card data, suppliment with article
    liveBlogLDJson = buildBlogPosting(card, article);
  } else if (isRegularBlog) {
    liveBlogLDJson = buildBlogPosting(article);
    if (article.body && Array.isArray(article.body)) {
      const articleBody = get(article, 'body[0].html', '');
      if (articleBody.length) {
        liveBlogLDJson.articleBody = articleBody;
      }
    }
  } else {
    // Use article data
    liveBlogLDJson = buildLiveBlog(article);
  }

  const newsArticleLdJson = buildNewsArticle(article, vertical);
  // remove dateCreated from LDJson on LiveBlog
  delete newsArticleLdJson.dateCreated;

  // set dateModified as mostRecent cards date on liveBlog
  const mostRecentCardDateModified = get(liveBlogLDJson, 'liveBlogUpdate[0].dateModified');
  newsArticleLdJson.dateModified = mostRecentCardDateModified || newsArticleLdJson.dateModified;

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(buildDataset(article, vertical)),
          }}
        />
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(newsArticleLdJson),
          }}
        />
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(liveBlogLDJson),
          }}
        />
      </Head>

      {constructMetaData(article, card, vertical, isRegularBlog)}
    </>
  );
}

export default getLiveBlogMetaData;
